import type { SerialCommitmentDto } from '../types';

/**
 * One serial, one open picklist at a time (owner, 2026-10-05) — the Allocate Bins dialog's pure half.
 *
 * NetSuite counts a serial on hand until its MIS posts, so the stock read lists serials that another open picklist
 * has already allocated or picked. The dialog hides those by default and shows them, labelled and never selectable,
 * behind "Show all serials". The server refuses one on its own; this is what keeps the user from choosing it.
 */

/** The subset of a stock row these functions read. */
export interface SerialRow {
  inventoryNumberId: number | null;
  quantityAvailable: number;
}

export type SerialCommitments = ReadonlyMap<number, SerialCommitmentDto>;

/** The serials to ask the server about: every on-hand row that names one, each once. */
export function serialIdsOf(rows: readonly SerialRow[]): number[] {
  const ids = new Set<number>();
  for (const row of rows) {
    if (row.inventoryNumberId != null && row.quantityAvailable > 0) ids.add(row.inventoryNumberId);
  }
  return [...ids].sort((a, b) => a - b);
}

/** The commitment on a row, or undefined when the row is free (or names no serial). */
export const commitmentOf = (row: SerialRow, commitments: SerialCommitments): SerialCommitmentDto | undefined =>
  row.inventoryNumberId == null ? undefined : commitments.get(row.inventoryNumberId);

/** Rows with stock, split into what may be allocated and what another picklist holds. Order is kept. */
export function splitByCommitment<T extends SerialRow>(
  rows: readonly T[],
  commitments: SerialCommitments,
): { free: T[]; committed: T[] } {
  const free: T[] = [];
  const committed: T[] = [];
  for (const row of rows) {
    if (row.quantityAvailable <= 0) continue;
    (commitmentOf(row, commitments) ? committed : free).push(row);
  }
  return { free, committed };
}

/**
 * The row's pill and its hover: the swap list's own words ("RESERVED · PL-…", "PICKED · PL-…"), so a contended
 * serial reads the same on every screen. Any other status still names the picklist.
 */
export function commitmentLabel(commitment: SerialCommitmentDto): { text: string; tone: string; title: string } {
  const picklist = commitment.picklistNumber;
  if (commitment.status === 'PickedElsewhere') {
    return {
      text: `PICKED · ${picklist}`,
      tone: 'bg-destructive/10 text-destructive',
      title: `Picked on ${picklist} and waiting to post. It cannot go on another picklist.`,
    };
  }
  return {
    text: `RESERVED · ${picklist}`,
    tone: 'bg-warning/10 text-warning',
    title: `Allocated to ${picklist}, not picked yet. It cannot go on another picklist while that one holds it.`,
  };
}

/** "1 free · 2 on other picklists" — what the switch's caption says. */
export const commitmentCaption = (free: number, committed: number): string =>
  `${free} free · ${committed} on ${committed === 1 ? 'another picklist' : 'other picklists'}`;
